import { useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  INFAKT_ENDPOINT, INFAKT_KIND_LABEL, docKey, fmtD, money, nowYM, packCSV, packText, plural, presetTemplates, totals, ymAdd,
  type InfaktKind, type InfaktMonthDoc, type InfaktTemplate, type PackNeeds,
} from '@biuro/shared';
import { PackNeedsError, api, type InfaktListed } from '../api.ts';
import { closeStatus, defaultCloseMonth, monthGen, monthLabel, monthLower, type CloseStep } from '../close.ts';
import { downloadFile, useCopy, useData, useMe, useNav, useSaveDoc, useDeleteDoc, useSettings, useToast } from '../hooks.tsx';
import { InvoiceForm } from '../forms/InvoiceForm.tsx';
import { PrintDataForm } from '../forms/PrintDataForm.tsx';
import { TemplateForm } from '../forms/TemplateForm.tsx';
import { Empty, Icon } from '../ui.tsx';
import { InvoiceRows, Totals } from './Uslugi.tsx';
import { MonthWizard } from './MonthWizard.tsx';
import { DONE, EntryDates, EntryLines, docPill, evaluateEntry, initialEntry, issuePayload, pct, templateKind } from './infaktEntry.tsx';


function Step({ n, step, children, aside }: { n: number; step: CloseStep; children: ReactNode; aside?: ReactNode }) {
  return (
    <section id={`sec-${step.key}`} className={`step ${step.state}`} aria-labelledby={`step-${step.key}`}>
      <div className="step-n" aria-hidden="true">{n}</div>
      <div className="step-body">
        <div className="step-head">
          <h2 id={`step-${step.key}`} data-n={n}>{step.label}</h2>
          <div className="actions"><span className="state">{step.text}</span>{aside}</div>
        </div>
        {children}
      </div>
    </section>
  );
}

export function Miesiac() {
  const { data } = useData();
  const st = useSettings();
  const [m, setM] = useState(defaultCloseMonth);
  const s = closeStatus(data, st, m);
  const def = defaultCloseMonth();
  const dueBits = [
    s.infaktDue ? `dokumenty w inFakcie do ${fmtD(s.infaktDue)}` : '',
    s.docsDue ? `paczka dla biura do ${fmtD(s.docsDue)}` : '',
  ].filter(Boolean);
  return (
    <>
      <div className="monthbar">
        <h1>{monthLabel(m)}</h1>
        <div className="monthnav">
          <button className="btn" aria-label="Poprzedni miesiąc" onClick={() => setM(ymAdd(m, -1))}><Icon.prev /></button>
          <button className="btn" aria-label="Następny miesiąc" disabled={m >= nowYM()} onClick={() => setM(ymAdd(m, 1))}><Icon.next /></button>
          {m !== def && <button className="btn ghost" onClick={() => setM(def)}>Wróć do {monthGen(def).split(' ')[0]}</button>}
        </div>
      </div>
      <p className="month-sub">Zamknięcie miesiąca w trzech krokach{dueBits.length ? `: ${dueBits.join(', ')}` : ''}.</p>
      <div className="progress">
        {s.steps.map((x, i) => {
          const I = x.state === 'ok' ? Icon.check : x.state === 'late' ? Icon.alert : null;
          return (
            <button key={x.key} type="button" className={x.state} onClick={() => document.getElementById(`sec-${x.key}`)?.scrollIntoView({ behavior: 'smooth' })}>
              <span className="ic">{I ? <I /> : i + 1}</span>
              <span><b>{x.label}</b><span className="s">{x.text}</span></span>
            </button>
          );
        })}
      </div>
      <Step n={1} step={s.steps[0]}><InfaktStep key={`i_${m}`} ym={m} /></Step>
      <Step n={2} step={s.steps[1]}><ForeignStep key={`f_${m}`} m={m} /></Step>
      <Step n={3} step={s.steps[2]}><SendStep key={`s_${m}`} m={m} /></Step>
    </>
  );
}

// ================= 1. inFakt =================
function InfaktStep({ ym }: { ym: string }) {
  const { data } = useData();
  const me = useMe().data;
  const save = useSaveDoc();
  const toast = useToast();
  const [edit, setEdit] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [wizard, setWizard] = useState(false);
  const [found, setFound] = useState<Record<string, InfaktListed[]> | null>(null);
  const qc = useQueryClient();
  // Najpierw to, co jeszcze trzeba wystawić; wystawione i nieaktywne na końcu.
  const rank = (id: string, t: InfaktTemplate) => (t.active === false ? 2 : DONE.includes(data.infaktDocs[docKey(id, ym)]?.status || '') ? 1 : 0);
  const templates = Object.entries(data.infaktTemplates).sort((a, b) =>
    rank(a[0], a[1]) - rank(b[0], b[1]) || a[1].kind.localeCompare(b[1].kind) || a[1].name.localeCompare(b[1].name, 'pl'));
  const check = async () => {
    setChecking(true);
    try {
      const r = await api.infaktCheck(ym);
      setFound(r.internal);
      await qc.invalidateQueries({ queryKey: ['data'] });
      toast(`Sprawdzono w inFakcie: gotowe ${r.found} z ${r.checked}.`);
    } catch (e) {
      toast((e as Error).message);
    } finally {
      setChecking(false);
    }
  };

  const addPresets = () => {
    for (const [id, t] of Object.entries(presetTemplates())) save.mutate({ col: 'infaktTemplates', id, doc: t });
    toast('Dodano 4 szablony. Uzupełnij klientów i stawki.');
  };

  return (
    <>
      {!me?.infakt.enabled && (
        <div className="note warn">
          Brak klucza API inFaktu na serwerze. Wygeneruj go w inFakcie (Ustawienia → API) i wpisz w pliku .env jako INFAKT_API_KEY. Szablony możesz przygotować już teraz.
        </div>
      )}
      {me?.infakt.sandbox && <div className="note hi">Tryb testowy: dokumenty trafiają do sandboxa inFaktu.</div>}
      {templates.length > 0 && (
        <div className="actions">
          {me?.infakt.enabled && <button className="btn pri" onClick={() => setWizard(true)}><Icon.next />Wystaw dokumenty za {monthLower(ym)}</button>}
          <button className="btn" disabled={!me?.infakt.enabled || checking} onClick={check}>{checking ? 'Sprawdzam…' : 'Sprawdź w inFakcie'}</button>
          <button className="btn ghost" onClick={() => setEdit('')}><Icon.plus />Dodaj szablon</button>
        </div>
      )}
      {!templates.length ? (
        <div className="panel">
          <Empty title="Brak szablonów">
            Szablon to dokument, który wystawiasz co miesiąc. Mogę od razu dodać cztery: fakturę wewnętrzną sprzedaży bez faktur (ryczałt 8,5% i 5,5%), dowód wewnętrzny nieodpłatnych świadczeń (3%), fakturę godzinową (12%) i fakturę miesięczną (8,5%).
            <div className="actions" style={{ marginTop: 12 }}>
              <button className="btn hi" onClick={addPresets}>Dodaj 4 szablony</button>
              <button className="btn" onClick={() => setEdit('')}>Własny szablon</button>
            </div>
          </Empty>
        </div>
      ) : (
        templates.map(([id, t]) => (
          <TemplateCard key={id} id={id} t={t} ym={ym} doc={data.infaktDocs[docKey(id, ym)]} listed={found?.[INFAKT_ENDPOINT[t.kind]]} onEdit={() => setEdit(id)} enabled={!!me?.infakt.enabled} />
        ))
      )}
      <RawPreview enabled={!!me?.infakt.enabled} />
      {edit !== null && <TemplateForm id={edit || undefined} onClose={() => setEdit(null)} />}
      {wizard && <MonthWizard ym={ym} enabled={!!me?.infakt.enabled} onClose={() => setWizard(false)} />}
    </>
  );
}

function TemplateCard({ id, t, ym, doc, listed, onEdit, enabled }: {
  id: string; t: InfaktTemplate; ym: string; doc?: InfaktMonthDoc; listed?: InfaktListed[]; onEdit: () => void; enabled: boolean;
}) {
  const save = useSaveDoc();
  const toast = useToast();
  const copy = useCopy();
  const qc = useQueryClient();
  const [entry, setEntry] = useState(() => initialEntry(t, ym, doc));
  const [number, setNumber] = useState('');
  const [busy, setBusy] = useState(false);
  const { values, calc, sum, inputProblem } = evaluateEntry(t, entry);
  const closed = !!doc && DONE.includes(doc.status);
  const off = t.active === false;
  /** Dlaczego nie można wystawić – pokazywane przy przycisku, żeby nieaktywny przycisk nie był zagadką. */
  const issueProblem = inputProblem
    || (doc?.status === 'pending' ? doc.message || 'Dokument jest w trakcie wystawiania.' : '')
    || (t.kind === 'invoice' && !enabled ? 'inFakt nie jest połączony – brak INFAKT_API_KEY na serwerze.' : '');
  // Brak klienta opisuje uwaga na górze karty.
  const canIssue = !issueProblem && !(t.kind === 'invoice' && !t.clientId);
  const hint = (text: string) => text && !busy ? <div className="hint act-hint">{text}</div> : null;

  const issue = async () => {
    setBusy(true);
    try {
      const r = await api.infaktIssue(issuePayload(id, t, ym, entry, values));
      toast(r.message || (r.status === 'error' ? 'inFakt odrzucił dokument.' : 'Wystawiono w inFakcie.'));
    } catch (e) {
      toast((e as Error).message);
    } finally {
      await qc.invalidateQueries({ queryKey: ['data'] });
      setBusy(false);
    }
  };

  const markManual = (num?: string, uuid?: string) => {
    save.mutate({ col: 'infaktDocs', id: docKey(id, ym), doc: {
      templateId: id, ym, status: 'manual', values, net: sum.net, number: (num ?? number).trim() || undefined, uuid, at: new Date().toISOString(),
    } satisfies InfaktMonthDoc });
    toast('Oznaczono jako wystawiony');
  };
  const reopen = () => save.mutate({ col: 'infaktDocs', id: docKey(id, ym), doc: { templateId: id, ym, status: 'error', values, at: new Date().toISOString(), message: 'Otwarty ponownie.' } satisfies InfaktMonthDoc });

  const copyText = () => copy([
    `${INFAKT_KIND_LABEL[t.kind]}: ${t.name} za ${monthLower(ym)}`,
    ...calc.map((c) => `${c.name}: netto ${money(c.net)}, VAT ${pct(c.vatSymbol)} ${money(c.vat)}, brutto ${money(c.gross)}, ryczałt ${pct(c.flat)}`),
    `Razem: netto ${money(sum.net)}, VAT ${money(sum.vat)}, brutto ${money(sum.gross)}`,
  ].join('\n'));
  const dates = <EntryDates t={t} entry={entry} onChange={(patch) => setEntry((p) => ({ ...p, ...patch }))} />;

  return (
    <div className={`panel pad tcard ${off ? 'off' : ''}`}>
      <div className="tcard-head">
        <div>
          <h3>{t.name}</h3>
          <div className="kind">{templateKind(t)}</div>
        </div>
        <div className="chips">{off ? <span className="pill">nieaktywny</span> : docPill(doc)}<button className="btn sm ghost" onClick={onEdit}>Edytuj</button></div>
      </div>
      {doc?.status === 'error' && doc.message && <div className="note bad">{doc.message}</div>}
      {t.kind === 'invoice' && !t.clientId && <div className="note warn">Wybierz w szablonie klienta z inFaktu.</div>}

      <EntryLines t={t} entry={entry} disabled={closed} onVals={(vals) => setEntry((p) => ({ ...p, vals }))} />

      {!closed && !off && (t.kind === 'invoice' || enabled ? (
        <div className="tcard-act">
          {dates}
          <button className="btn pri" disabled={busy || !canIssue} onClick={issue}>{busy ? 'Wystawiam…' : 'Wystaw w inFakcie'}</button>
        </div>
      ) : (
        <div className="tcard-act">
          <button className="btn" onClick={copyText}>Kopiuj kwoty</button>
          <a className="btn" href="https://app.infakt.pl" target="_blank" rel="noreferrer"><Icon.ext />Otwórz inFakt</a>
          <label className="mini"><span>Numer w inFakcie</span><input value={number} onChange={(e) => setNumber(e.target.value)} placeholder="opcjonalnie" /></label>
          <button className="btn pri" disabled={!!inputProblem} onClick={() => markManual()}>Oznacz jako wystawiony</button>
        </div>
      ))}
      {!closed && !off && hint(t.kind === 'invoice' || enabled ? issueProblem : inputProblem)}
      {!closed && listed && listed.length > 0 && t.kind !== 'invoice' && (
        <div className="note">
          <div style={{ marginBottom: 6 }}>Te dokumenty tego rodzaju są w inFakcie za {monthLower(ym)}. Wskaż, który dotyczy tego szablonu:</div>
          <div className="pick">{listed.map((d) => <button key={d.uuid} className="btn sm" onClick={() => markManual(d.number, d.uuid)}>{d.number}, {fmtD(d.date)}</button>)}</div>
        </div>
      )}
      {closed && <div className="tcard-act"><span className="muted small">{doc?.message || ''}</span><button className="btn sm ghost" onClick={reopen}>Otwórz ponownie</button></div>}
    </div>
  );
}

function RawPreview({ enabled }: { enabled: boolean }) {
  const [kind, setKind] = useState<InfaktKind>('internal_invoice');
  const [open, setOpen] = useState(false);
  const [uuid, setUuid] = useState('');
  const [text, setText] = useState('');
  // Lista z inFaktu dopiero po rozwinięciu narzędzia – bez zapytań przy każdym wejściu na stronę.
  const docs = useQuery({ queryKey: ['infaktRawDocs', kind], queryFn: () => api.infaktDocs(kind), enabled: open && enabled, staleTime: 60_000, retry: false });
  const toast = useToast();
  const copy = useCopy();
  const load = async () => {
    try {
      const r = await api.infaktLatest(kind, uuid);
      setText(r.data ? JSON.stringify(r.data, null, 2) : 'Brak dokumentów tego rodzaju w inFakcie.');
    } catch (e) {
      toast((e as Error).message);
    }
  };
  return (
    <details className="raw" onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary>Format dokumentów wewnętrznych w inFakcie</summary>
      <p className="muted small">Podgląd surowego JSON-u dokumentu z Twojego konta. Przydaje się, gdy inFakt odrzuci wystawiany dokument i trzeba porównać pola z dokumentem wystawionym ręcznie.</p>
      <div className="actions">
        <select value={kind} onChange={(e) => { setKind(e.target.value as InfaktKind); setUuid(''); }} aria-label="Rodzaj dokumentu">
          <option value="internal_invoice">Faktura wewnętrzna</option>
          <option value="internal_evidence">Dowód wewnętrzny</option>
        </select>
        <label className="mini">
          <span>Dokument</span>
          <select value={uuid} onChange={(e) => setUuid(e.target.value)} aria-label="Dokument">
            <option value="">{docs.isLoading ? 'Wczytuję listę…' : 'Najnowszy'}</option>
            {docs.data?.map((d) => <option key={d.uuid} value={d.uuid}>{d.number}, {fmtD(d.date)}</option>)}
          </select>
        </label>
        <button className="btn" disabled={!enabled} onClick={load}>Pobierz JSON</button>
        {text && <button className="btn" onClick={() => copy(text)}>Kopiuj</button>}
      </div>
      {text && <pre className="code">{text}</pre>}
    </details>
  );
}

// ================= 2. Faktury zagraniczne =================
function ForeignStep({ m }: { m: string }) {
  const { data } = useData();
  const st = useSettings();
  const { go } = useNav();
  const [edit, setEdit] = useState<string | null>(null);
  const folder = useQuery({ queryKey: ['folder', m], queryFn: () => api.monthFolder(m), staleTime: 60_000 });
  const s = closeStatus(data, st, m);
  const tt = totals(s.rows.map((x) => x.c));
  return (
    <>
      {s.missing.length > 0 && (
        <div className="note warn">
          Brakuje faktur za {monthLower(m)}: {s.missing.map((x) => x.s.name).join(', ')}.{' '}
          <button className="linkish" onClick={() => setEdit('')}>Dodaj fakturę</button>
        </div>
      )}
      {s.drafts > 0 && (
        <div className="note warn">
          {s.drafts} {plural(s.drafts, 'faktura czeka', 'faktury czekają', 'faktur czeka')} na uzupełnienie kwoty i dat. Do tego czasu nie ma jej w zestawieniu.{' '}
          <button className="linkish" onClick={() => go('faktury', { sub: 'draft' })}>Uzupełnij</button>
        </div>
      )}
      {tt.missing > 0 && <div className="note warn">{tt.missing} {plural(tt.missing, 'faktura nie ma', 'faktury nie mają', 'faktur nie ma')} kursu NBP i nie wchodzi do sum.</div>}
      {s.rows.length ? (
        <>
          <InvoiceRows rows={s.rows} onEdit={setEdit} />
          <Totals rows={s.rows} />
        </>
      ) : (
        <div className="panel"><Empty title={`Brak faktur z obowiązkiem podatkowym w miesiącu ${monthLower(m)}`}>Jeśli powinny być, dodaj je tutaj albo w zakładce Faktury zagraniczne.</Empty></div>
      )}
      <div className="actions">
        <button className="btn" onClick={() => setEdit('')}><Icon.plus />Dodaj fakturę</button>
        {folder.data?.link && <a className="btn ghost" href={folder.data.link} target="_blank" rel="noreferrer"><Icon.ext />Folder na Dysku</a>}
      </div>
      {edit !== null && <InvoiceForm id={edit || undefined} onClose={() => setEdit(null)} />}
    </>
  );
}

// ================= 3. Wysyłka do biura =================
function SendStep({ m }: { m: string }) {
  const { data } = useData();
  const st = useSettings();
  const save = useSaveDoc();
  const del = useDeleteDoc();
  const toast = useToast();
  const copy = useCopy();
  const [range, setRange] = useState(false);
  const [from, setFrom] = useState(m);
  const [to, setTo] = useState(m);
  const [busy, setBusy] = useState(false);
  /** Braki danych do wydruków z Biura – okno przed pobraniem paczki. */
  const [needs, setNeeds] = useState<PackNeeds | null>(null);
  const folder = useQuery({ queryKey: ['folder', m], queryFn: () => api.monthFolder(m), staleTime: 60_000 });
  const s = closeStatus(data, st, m);
  const withFile = s.rows.filter((x) => x.inv.fileId).length;
  const sentAt = data.done[`docs-${m}`]?.at;

  const download = async (skipPrint = false) => {
    const a = range ? from : m, b = range ? to : m;
    if (b < a) return toast('Koniec okresu jest przed początkiem.');
    setBusy(true);
    try {
      const r = await api.pack(a, b, skipPrint);
      setNeeds(null);
      downloadFile(r.filename, r.blob, 'application/zip');
      const x = r.summary;
      const printed = x.printed ? `, ${x.printed} ${plural(x.printed, 'wydruk', 'wydruki', 'wydruków')} z Biura` : '';
      toast(`Pobrano paczkę: ${x.invoiceFiles} ${plural(x.invoiceFiles, 'faktura', 'faktury', 'faktur')}, ${x.internal} ${plural(x.internal, 'dokument wewnętrzny', 'dokumenty wewnętrzne', 'dokumentów wewnętrznych')}${printed}${x.missing ? `. Braki: ${x.missing}, szczegóły w BRAKI.txt` : ''}.`);
    } catch (e) {
      if (e instanceof PackNeedsError) setNeeds(e.needs);
      else toast((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const markSent = () => {
    const at = new Date().toISOString();
    for (const x of s.rows) if ((x.inv.status || 'new') === 'new') save.mutate({ col: 'invoices', id: x.id, doc: { ...x.inv, status: 'sent', sentAt: at } });
    save.mutate({ col: 'done', id: `docs-${m}`, doc: { at } });
    toast(`Oznaczono jako wysłane: ${monthLower(m)}`);
  };
  const unmark = () => del.mutate({ col: 'done', id: `docs-${m}` });

  const subject = `Dokumenty za ${monthLower(m)}${st.companyName ? ` – ${st.companyName}` : ''}`;
  const body = `Dzień dobry,\n\nw załączniku paczka dokumentów za ${monthLabel(m)}: faktury zagraniczne, dokumenty wewnętrzne z inFaktu i zestawienie faktur.\n\nPozdrawiam`;
  const mailto = st.accountantEmail ? `mailto:${st.accountantEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}` : '';

  return (
    <>
      <div className="panel pad pack">
        <div>
          <b>Paczka ZIP dla biura</b>
          <ul>
            <li>PDF-y faktur zagranicznych z Dysku ({withFile} z {s.rows.length})</li>
            <li>faktury wewnętrzne i dowody wewnętrzne z inFaktu</li>
            <li>zestawienie faktur (CSV) i podsumowanie</li>
            <li>lista braków, jeśli czegoś brakuje</li>
          </ul>
        </div>
        <div className="stack" style={{ alignItems: 'flex-end' }}>
          {range && (
            <div className="pack-range">
              <label className="mini"><span>Od</span><input type="month" value={from} max={to} onChange={(e) => setFrom(e.target.value)} /></label>
              <label className="mini"><span>Do</span><input type="month" value={to} min={from} onChange={(e) => setTo(e.target.value)} /></label>
            </div>
          )}
          <div className="actions">
            <button className="btn ghost sm" onClick={() => setRange((v) => !v)}>{range ? `Tylko ${monthLower(m)}` : 'Inny okres'}</button>
            <button className="btn hi" disabled={busy} onClick={() => void download()}><Icon.download />{busy ? 'Przygotowuję…' : 'Pobierz paczkę'}</button>
          </div>
        </div>
      </div>

      {needs && <PrintDataForm needs={needs} onClose={() => setNeeds(null)} onSaved={() => download()} onSkip={() => download(true)} />}

      <div className="panel pad stack">
        {st.accountantName || st.accountantEmail ? (
          <div className="contact">
            <span><b>{st.accountantName || 'Biuro rachunkowe'}</b>{st.accountantEmail ? <span className="muted"> {st.accountantEmail}</span> : null}</span>
            {st.accountantEmail && <button className="btn sm ghost" onClick={() => copy(st.accountantEmail)}>Kopiuj adres</button>}
          </div>
        ) : <div className="muted small">Dodaj dane biura rachunkowego w zakładce Firma, a przycisk poniżej otworzy gotowego maila.</div>}
        <div className="actions">
          {mailto && <a className="btn" href={mailto}><Icon.mail />Napisz do biura</a>}
          <button className="btn" onClick={() => copy(packText(data, m, folder.data?.link || undefined))}>Kopiuj zestawienie</button>
          <button className="btn ghost" onClick={() => downloadFile(`import-uslug-${m}.csv`, packCSV(data, m), 'text/csv;charset=utf-8')}>Tylko CSV</button>
        </div>
        <div className="sync-row">
          {sentAt
            ? <><span className="pill ok">wysłane {fmtD(new Date(sentAt))}</span><button className="btn sm ghost" onClick={unmark}>Cofnij</button></>
            : <><span className="muted small">{s.docsDue ? `Termin dla biura: ${fmtD(s.docsDue)}.` : ''} Po wysłaniu faktury dostaną status „w biurze”, a termin zostanie odhaczony.</span><button className="btn" onClick={markSent}>Oznacz jako wysłane</button></>}
        </div>
      </div>
      <p className="muted small" style={{ maxWidth: '70ch', margin: 0 }}>Kwoty w złotych są przeliczone po średnim kursie NBP (tabela A) z ostatniego dnia roboczego przed datą zapłaty albo końcem okresu usługi.</p>
    </>
  );
}
